import {lazyInject} from "@athena-portal/core";
import {
    Policy,
    productHelper,
    ProductsEnum,
    POLICY_STATUS_PENDING_RENEWAL,
} from "@ferry/journey-services";
import * as React from "react";
import {Checkbox} from "../../../../../../components/inputs/Checkbox";
import {AutomaticRenewalCancellationConfirmationModal, AutoRenewalBenefits} from "@ferry/plugin-ferry-journey";
import {RenewalPreferences} from "../../../../../../components/enums/enums";
import {Modal, ModalBody, ModalHeader, ModalFooter} from "reactstrap";
import {FalconAccordion} from "../../../../../../components/FalconAccordion";
import {AutomaticRenewalSuccessModal} from "../../../../../../components/modals/AutomaticRenewalSuccessModal";
import {GracePeriodModal} from "../../../../../../components/banners/GracePeriodModal";
import * as moment from "moment";
import {NavigateFunction} from "react-router-dom";
import type {ICustomerPortalService} from "../../../../../../services/ICustomerPortalService";
import {Types} from "../../../../../../Types";
import {InfoBanner, resourcesHelper} from "@ferry/portal-core";

interface IAutomaticRenewalCheckboxSectionProps {
    activeAnnualPolicy?: Policy;
    automaticRenewal: boolean;
    performRenewal: boolean;
    twoDaysGracePeriod: boolean;
    renewalProposal: any;
    navigate: NavigateFunction;
}

interface IAutomaticRenewalCheckboxSectionState {
    automaticRenewal?: boolean;
    performRenewal?: boolean;
    showModal: boolean;
    renewalType: string;
    tmpRenewalType: string;
    showProposeModal: boolean;
    showSuccessModal: boolean;
    showGracePeriodBanner: boolean;
}

export class AutomaticRenewalCheckboxSection extends React.Component<IAutomaticRenewalCheckboxSectionProps, IAutomaticRenewalCheckboxSectionState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IAutomaticRenewalCheckboxSectionProps) {
        super(props);

        this.state = {
            showModal: false,
            showProposeModal: false,
            showSuccessModal: false,
            renewalType: this.setRenewalType(),
            tmpRenewalType: "",
            showGracePeriodBanner: false,
            automaticRenewal: this.props.automaticRenewal,
            performRenewal: this.props.performRenewal
        };

        this.showProposeModal = this.showProposeModal.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.changeTmpRenewalType = this.changeTmpRenewalType.bind(this);
        this.updateRenewalType = this.updateRenewalType.bind(this);
        this.setRenewalType = this.setRenewalType.bind(this);
    }

    public componentDidUpdate(prevProps: IAutomaticRenewalCheckboxSectionProps, prevState: IAutomaticRenewalCheckboxSectionState) {
        if (this.state.showProposeModal && !prevState.showProposeModal) {
            this.setState({tmpRenewalType: this.state.renewalType});
        }
    }

    public render() {
        const renewalPeriod: boolean = this.props.renewalProposal != null && this.props.renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;

        if (renewalPeriod) {
            return this.renderRenewalChoiceInRenewalWindow();
        } else {
            return this.renderRenewalChoice();
        }
    };

    private renderRenewalChoice(): any {
        return (
            <>
                <div className="mt-5 inner-container automatic-renewal-checkbox-section">
                    <h2 className="font-family-display">Auto-renewal makes
                        things a little easier...</h2>
                    <AutoRenewalBenefits/>
                    <div className="expandable-section">
                        <p className="font-family-base font-weight-bold fs-20 mb-0">Prefer not to
                            auto-renew?</p>
                        <div className="widget">
                            <FalconAccordion
                                toggleHidden={this.renderAccordionToggle("expand-icon")}
                                toggleShown={this.renderAccordionToggle("collapse-icon")}
                                togglePositionTop={true}
                            >
                                <div>
                                    <Checkbox
                                        className="ml-0 mt-0 pt-3"
                                        id="automaticRenewalCheckbox"
                                        name="automaticRenewal"
                                        label={this.getCheckboxLabel()}
                                        value={this.state.performRenewal!}
                                        onChanged={this.handleCheckboxToggle}
                                    />
                                </div>
                            </FalconAccordion>
                        </div>
                    </div>
                </div>
                <AutomaticRenewalCancellationConfirmationModal
                    isOpen={this.state.showModal}
                    onAgreeToAutoRenewal={this.keepRenewalPreferences}
                    onConfirmToCancelAutoRenewal={this.cancelNextYearRenewal}
                    product={ProductsEnum.WOLF_MOTOR}
                />
                {this.state.showSuccessModal && !(this.state.showGracePeriodBanner && this.props.twoDaysGracePeriod) &&
                    <AutomaticRenewalSuccessModal
                        automaticRenewal={this.state.automaticRenewal!}
                        nextYearRenewal={this.state.performRenewal!}
                        product={ProductsEnum.WOLF_MOTOR}
                        closeSuccessModal={this.closeSuccessModal}
                    />}
            </>
        )
    }

    private renderRenewalChoiceInRenewalWindow(): any {
        return (
            <>
                {this.renderPerformRenewalCheckboxContent()}
                {this.renderPerformRenewalModal(this.state.showProposeModal)}
            </>
        );
    }

    private renderPerformRenewalCheckboxContent(): any {

        const policyEndDate: any = moment.utc(this.props.activeAnnualPolicy?.endDate, moment.ISO_8601).add(1, 'days').format("DD MMM YYYY");
        const performLabelContent = (
            <>
                {this.state.renewalType !== RenewalPreferences.NO_RENEWAL && <div>
                    <span className="d-block mb-2 font-weight-semi-bold">I confirm that I want to schedule my annual car policy to renew.</span>
                    <InfoBanner wording={<>Heads up - your car insurance policy will automatically renew on {policyEndDate}, 00:00. No payment will be taken until then.</>}/>
                </div>}
                {this.state.renewalType === RenewalPreferences.NO_RENEWAL && <div>
                    <span className="d-block mb-2 font-weight-semi-bold">I confirm that I don't want to renew my annual car insurance.</span>
                    <InfoBanner wording={
                        <>
                            Heads up - <span className="font-weight-semi-bold">you'll no longer be covered starting from {policyEndDate}.</span>
                            <div className="margin-top-16">
                                If you change your mind, you can still renew your policy by choosing to edit renewal preferences below.
                            </div>
                        </>
                    }/>
                </div>}
                <div>
                    <button type="button"
                            className="btn btn-primary btn-next-no-arrow w-100 edit-renewal-preferences-btn"
                            onClick={this.showProposeModal}>Edit renewal preferences
                    </button>
                </div>
                {this.state.showSuccessModal && !(this.state.showGracePeriodBanner && this.props.twoDaysGracePeriod) &&
                    <AutomaticRenewalSuccessModal
                        automaticRenewal={this.state.automaticRenewal!}
                        nextYearRenewal={this.state.performRenewal!}
                        product={ProductsEnum.WOLF_MOTOR}
                        closeSuccessModal={this.closeSuccessModal}
                    />}
            </>

        );

        return (
            <div className="falcon-checkbox form-group">
                <div className="d-flex">
                    <div className="form-check mr-3">
                        <input className={`custom-control-input true`} type="checkbox"
                               name="performRenewal"
                               id="performRenewal"
                               checked={true}
                        />
                        <label className="custom-control-label" htmlFor="performRenewal">
                            <div className="checkbox-big"/>
                            <div className="checkbox-label">
                                {performLabelContent}
                            </div>
                        </label>
                    </div>
                </div>
            </div>
        );
    }

    private renderPerformRenewalModal(isOpen: boolean): any {
        return (
            <>
                <Modal className="automatic-renewal-modal pb-0" isOpen={isOpen} toggle={this.cancelChanges}
                       id="automaticRenewalModal">
                    <ModalHeader toggle={this.cancelChanges}>
                        <div className="info-popup-title">
                            <img src={`/${resourcesHelper.THEME_PATH}/img/info-popup.svg`} alt="Attention"/>
                            <span>Edit policy renewal preferences</span>
                        </div>
                    </ModalHeader>
                    <ModalBody className="pt-0">
                        <div className="form-group edit-renewal-preference-modal">
                            <div className="input-group">
                                <label className="modal-header-label">Do you want your annual car policy to
                                    auto-renew?</label>
                                <div className="custom-control w-100 falcon-custom-radio">
                                    <input
                                        className={"custom-radiobutton cursor-pointer"}
                                        type="radio"
                                        name={"performRenewal-" + RenewalPreferences.RENEWAL_THIS_YEAR}
                                        id={"performRenewal-" + RenewalPreferences.RENEWAL_THIS_YEAR}
                                        value={RenewalPreferences.RENEWAL_THIS_YEAR}
                                        checked={this.state.tmpRenewalType === RenewalPreferences.RENEWAL_THIS_YEAR}
                                        onChange={this.changeTmpRenewalType}
                                    />
                                    <label id={'driver-label'}
                                           className="checkbox-label label-bg-transparent font-weight-semi-bold"
                                           htmlFor={"performRenewal-" + RenewalPreferences.RENEWAL_THIS_YEAR}>
                                        Yes, renew this year only
                                    </label>
                                </div>
                                <div className="custom-control w-100 falcon-custom-radio">
                                    <input
                                        className={"custom-radiobutton cursor-pointer"}
                                        type="radio"
                                        name={"performRenewal-" + RenewalPreferences.RENEWAL_EVERY_YEAR}
                                        id={"performRenewal-" + RenewalPreferences.RENEWAL_EVERY_YEAR}
                                        value={RenewalPreferences.RENEWAL_EVERY_YEAR}
                                        checked={this.state.tmpRenewalType === RenewalPreferences.RENEWAL_EVERY_YEAR}
                                        onChange={this.changeTmpRenewalType}
                                    />
                                    <label id={'driver-label'}
                                           className="checkbox-label label-bg-transparent font-weight-semi-bold"
                                           htmlFor={"performRenewal-" + RenewalPreferences.RENEWAL_EVERY_YEAR}>
                                        Yes, auto-renew every year
                                    </label>
                                </div>
                                <div className="custom-control w-100 falcon-custom-radio">
                                    <input
                                        className={"custom-radiobutton cursor-pointer"}
                                        type="radio"
                                        name={"performRenewal-" + RenewalPreferences.NO_RENEWAL}
                                        id={"performRenewal-" + RenewalPreferences.NO_RENEWAL}
                                        value={RenewalPreferences.NO_RENEWAL}
                                        checked={this.state.tmpRenewalType === RenewalPreferences.NO_RENEWAL}
                                        onChange={this.changeTmpRenewalType}
                                    />
                                    <label id={'driver-label'}
                                           className="checkbox-label label-bg-transparent font-weight-semi-bold"
                                           htmlFor={"performRenewal-" + RenewalPreferences.NO_RENEWAL}>
                                        No, I don't want to auto-renew
                                    </label>
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <div className="renewalModalButtonContainer">
                            <button type="button" className="btn btn-primary btn-next-no-arrow w-100"
                                    onClick={this.updateRenewalType}>Save changes
                            </button>
                            <button type="button" className="btn btn-white mb-0 w-100" onClick={this.cancelChanges}
                                    id="noAutoRenewal">Cancel changes
                            </button>
                        </div>
                    </ModalFooter>
                </Modal>
                {this.props.twoDaysGracePeriod && this.state.showGracePeriodBanner &&
                    <GracePeriodModal
                        currentPolicy={this.props.activeAnnualPolicy!}
                        renewalProposal={this.props.renewalProposal!}
                        hideCloseButton={true}
                        setPerformRenewal={() => {
                        }}
                        showHideWillBeRenewedConfirmation={() => {
                        }}
                        onClose={() => {
                            this.update(false, false);
                            this.setState({showGracePeriodBanner: false, renewalType: RenewalPreferences.NO_RENEWAL})
                        }}
                    />
                }
            </>
        );
    }

    private getCheckboxLabel() {
        return (
            <div><p>By automatically renewing your insurance, you can save time next year and ensure that there's no
                interruption to your cover.<br/></p>
                <p className="mb-1">Untick this button to opt out of automatic renewal.</p></div>
        );
    }

    private renderAccordionToggle(accordionIcon: string) {
        return (
            <div className="text-left cursor-pointer d-flex align-items-center">
                <span className={`toggle-icon mr-2 ${accordionIcon}`}/>
                <span className="btn-text">Opt out here</span>
            </div>
        )
    }

    private handleCheckboxToggle = (newValue: boolean) => {
        if (newValue) {
            this.setState(
                {
                    automaticRenewal: true,
                    performRenewal: true,
                    showSuccessModal: true
                },
                () => this.update(this.state.automaticRenewal!, this.state.performRenewal!)
            );
        } else {
            this.setState({
                showModal: true
            })
        }
    };

    private keepRenewalPreferences = () => {
        this.setState({
            showModal: false,
            showSuccessModal: true
        });
    };

    private cancelNextYearRenewal = () => {
        if (productHelper.isPolicyInRenewalWindow(this.props.activeAnnualPolicy!)) {
            this.props.navigate("/dashboard/annualMotorCancellationReason");
        } else {
            this.setState(
                {
                    automaticRenewal: false,
                    performRenewal: false,
                    showModal: false,
                    showSuccessModal: true
                },
                () => this.update(this.state.automaticRenewal!, this.state.performRenewal!)
            );
        }
    };

    private setRenewalType(): string {
        const renewalPeriod: boolean = this.props.renewalProposal != null && this.props.renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;
        const {performRenewal, automaticRenewal} = this.props;

        if (renewalPeriod) {
            if (performRenewal && automaticRenewal) {
                return RenewalPreferences.RENEWAL_EVERY_YEAR;
            } else if (performRenewal && !automaticRenewal) {
                return RenewalPreferences.RENEWAL_THIS_YEAR;
            } else {
                return RenewalPreferences.NO_RENEWAL;
            }
        } else {
            return "";
        }
    }

    private showProposeModal = () => {
        this.setState({
            showProposeModal: true
        });
    };

    private cancelChanges = () => {
        this.setState({
            showProposeModal: false
        });
    };

    private changeTmpRenewalType = (event: any) => {
        this.setState({tmpRenewalType: event.target.value});
    };

    private closeSuccessModal = (): void => {
        this.setState({
            showSuccessModal: false
        });
        window.location.reload();
    };

    private updateRenewalType = () => {
        this.setState({renewalType: this.state.tmpRenewalType});
        this.updatePropertyAndState(this.state.tmpRenewalType);
    };

    private updatePropertyAndState(renewalType: string) {
        let automaticRenewal = false;
        let performRenewal = false;
        switch (renewalType) {
            case RenewalPreferences.RENEWAL_THIS_YEAR:
                performRenewal = true;
                break;
            case RenewalPreferences.RENEWAL_EVERY_YEAR:
                automaticRenewal = true;
                performRenewal = true;
                break;
        }

        this.update(automaticRenewal, performRenewal);

        this.setState({
            showProposeModal: false,
            automaticRenewal,
            performRenewal,
            showSuccessModal: true,
            showGracePeriodBanner: renewalType !== RenewalPreferences.NO_RENEWAL,
            renewalType
        });
    }

    private update = (automaticRenewal: boolean, performRenewal: boolean) => {
        this.customerPortalService.storePolicyPerformRenewalFlag(this.props.activeAnnualPolicy!.policyNumber, performRenewal).toPromise().then(() => {
            return this.setState({
                performRenewal
            });
        });

        this.customerPortalService.storePolicyAutomaticRenewalFlag(this.props.activeAnnualPolicy!.policyNumber, automaticRenewal).toPromise().then(() => {
                return this.setState({
                    automaticRenewal
                });
            }
        );
    }
}
